import * as Phaser from 'phaser';
import { isNeutralGrade, type Grade } from '../../sim/environment/grade.ts';

/**
 * Day/night and overcast grading, applied as one ColorMatrix on the camera so it covers every
 * layer at once — water, land, the boat and the debug overlays all dim together.
 *
 * Filters are WebGL-only. Under Canvas there is no grading rather than a half-applied
 * approximation: the map still plays, it just always looks like noon.
 */
export class CameraGrading {
  private readonly camera: Phaser.Cameras.Scene2D.Camera;
  private filter: Phaser.Filters.ColorMatrix | null = null;

  constructor(camera: Phaser.Cameras.Scene2D.Camera) {
    this.camera = camera;
  }

  apply(grade: Grade): void {
    if (this.camera.scene.renderer.type !== Phaser.WEBGL) {
      return;
    }

    if (isNeutralGrade(grade)) {
      this.filter?.setActive(false);
      return;
    }

    const filter = this.filter ?? this.camera.filters?.internal.addColorMatrix() ?? null;

    if (!filter) {
      return;
    }

    this.filter = filter;
    filter.setActive(true);

    // Reset first: every ColorMatrix operation here multiplies onto the matrix already in place,
    // so without it each re-grade would compound the previous one.
    filter.colorMatrix.reset();
    filter.colorMatrix.saturate(grade.saturate, true);
    filter.colorMatrix.brightness(1 + grade.brightness, true);

    if (grade.tintAmount > 0) {
      filter.colorMatrix.multiply(tintMatrix(grade.tint, grade.tintAmount), true);
    }
  }

  destroy(): void {
    this.filter?.destroy();
    this.filter = null;
  }
}

/**
 * Scales each channel towards the tint's channel ratios, normalised so the tint's own brightest
 * channel is left untouched. Without that normalisation a dark tint doubles as a second brightness
 * cut on top of `grade.brightness`, and a strong night grade collapses the scene to flat navy.
 * Phaser's `night()` preset is a night-vision look rather than a colour shift, so it is not usable.
 */
function tintMatrix(tint: number, amount: number): number[] {
  const color = Phaser.Display.Color.IntegerToColor(tint);
  const peak = Math.max(color.red, color.green, color.blue, 1);
  const r = Phaser.Math.Linear(1, color.red / peak, amount);
  const g = Phaser.Math.Linear(1, color.green / peak, amount);
  const b = Phaser.Math.Linear(1, color.blue / peak, amount);

  return [
    r, 0, 0, 0, 0,
    0, g, 0, 0, 0,
    0, 0, b, 0, 0,
    0, 0, 0, 1, 0,
  ];
}
